/**
 * Enhanced IconButton component
 * For buttons that only display an icon
 */

import React from 'react';
import {
  IconButton as ChakraIconButton,
  IconButtonProps as ChakraIconButtonProps,
  forwardRef,
  Spinner,
  Tooltip,
} from '@chakra-ui/react';
import {
  BaseComponentProps,
  ComponentSize,
  ComponentVariant,
} from '../../types';

export interface IconButtonProps
  extends BaseComponentProps,
    Omit<ChakraIconButtonProps, 'size' | 'variant' | 'aria-label'> {
  /** Button variant/color scheme */
  variant?: ComponentVariant;
  /** Button size */
  size?: ComponentSize;
  /** Whether button is in loading state */
  loading?: boolean;
  /** Icon to display */
  icon: React.ReactElement;
  /** Accessible label for the button */
  label: string;
  /** Tooltip text (defaults to label if not provided) */
  tooltip?: string;
  /** Whether to show tooltip */
  showTooltip?: boolean;
}

const variantMap: Record<ComponentVariant, string> = {
  primary: 'blue',
  secondary: 'gray',
  success: 'green',
  warning: 'orange',
  danger: 'red',
  info: 'teal',
  outline: 'gray',
  ghost: 'gray',
};

export const IconButton = forwardRef<IconButtonProps, 'button'>(
  (props, ref) => {
    const {
      variant = 'secondary',
      size = 'md',
      loading = false,
      icon,
      label,
      tooltip,
      showTooltip = true,
      className,
      testId,
      disabled,
      ...rest
    } = props;

    const button = (
      <ChakraIconButton
        ref={ref}
        colorScheme={variantMap[variant]}
        size={size}
        icon={
          loading ? (
            <Spinner size={size === 'xs' || size === 'sm' ? 'xs' : 'sm'} />
          ) : (
            icon
          )
        }
        aria-label={label}
        isDisabled={disabled || loading}
        className={className}
        data-testid={testId}
        {...rest}
      />
    );

    if (showTooltip) {
      return (
        <Tooltip label={tooltip || label} hasArrow>
          {button}
        </Tooltip>
      );
    }

    return button;
  },
);

IconButton.displayName = 'IconButton';
